import { Injectable } from "@angular/core";
import {
  PRIMITIVE,
  ServiceMessageBrokerFactory,
  ServiceMessageBroker,
} from "shared/provider-webworker";

import { CurrencyPairGroupLookupService } from "./CurrencyPairGroupLookupService";
import { PrimaryLookupService } from "../../impl/PrimaryLookupService";
import { ICurrencyPairGroupPromiseLookupService } from "../ICurrencyPairGroupPromiseLookupService";
import { BaseLookupService } from "../../impl/BaseLookupService";
import { PromiseLookupService } from "../../impl/PromiseLookupService";

import { CubiqWorker } from "../../../worker/CubiqWorker";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Injectable()
export class CurrencyPairGroupPrimaryLookupService
  extends PrimaryLookupService<string, CubiqTS.CurrencyPairGroupImpl>
  implements ICurrencyPairGroupPromiseLookupService
{
  protected pairIdx: Map<Number, Set<string>> = new Map();

  constructor(
    protected base: CurrencyPairGroupLookupService,
    protected factory: ServiceMessageBrokerFactory,
    protected cubiqWorker: CubiqWorker,
  ) {
    super(base, factory, CubiqTS.CurrencyPairGroupImpl, cubiqWorker, "CurrencyPairGroupImpl");

    cubiqWorker.isAuth(`${this.constructor.name}`).then(
      () => {
        let CurrencyPairGroupsObservable =
          cubiqWorker.primeStaticData<CubiqTS.CurrencyPairGroupImpl>(
            CubiqTS.CurrencyPairGroupImpl.name,
          );

        CurrencyPairGroupsObservable.subscribe(
          (CurrencyPairGroups: CubiqTS.CurrencyPairGroupImpl[]) => {
            if (Array.isArray(CurrencyPairGroups)) {
              this.base.prime(CurrencyPairGroups);
            } else {
              this.base.prime([]);
            }
          },
          (error: Error) => {
            logger.out.warn("CurrencyPair prime failed");
            this._primed.reject(error);
          },
          () => {
            this._primed.resolve(true);
          },
        );
      },
      (reason) => {
        logger.out.warn("CurrencyPairGroupPrimaryLookupService: Failed to prime: " + reason);
        this._primed.resolve(false);
      },
    );

    this.indexData();
  }

  protected processModelUpdateEvent(event: CubiqTS.ModelUpdateImpl): void {
    super.processModelUpdateEvent(event);
    this.indexData();
  }

  indexData(): void {
    this.isPrimed().then((primed) => {
      this.getAll().then((found) => {
        if (found)
          found.forEach((pairGrp) => {
            this.indexItem(pairGrp);
          });
      });
    });
  }

  /**
   * we need to build some additional indices
   * so we can get the pairs for a group id
   */
  indexItem(group: CubiqTS.CurrencyPairGroupImpl): void {
    let id: number = group.id.toNumber(); // Map didn't like the Long as a key
    let groupPairs: Set<string> = this.pairIdx.get(id);
    if (null == groupPairs) {
      groupPairs = new Set<string>();
      this.pairIdx.set(id, groupPairs);
    }
    if (group.groupMembership)
      group.groupMembership.map((pair: CubiqTS.CurrencyPairGroupMember) => {
        let productKey: string = pair.baseCurrencyCd + pair.nonBaseCurrencyCd;
        groupPairs.add(productKey);
      });
  }

  getPairs(groupId: Number): string[] {
    let ret: string[] = [];
    let found: Set<string> = this.pairIdx.get(groupId);
    if (found) ret = Array.from(found); // so nobody screws with the cache
    return ret;
  }
}
